import { useState } from 'react';
import { Link as RouterLink, NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  Activity,
  ChevronRight,
  Building2,
  Boxes,
  FileArchive,
  FileBarChart,
  GitBranch,
  History,
  LayoutDashboard,
  Layers,
  LogOut,
  Menu,
  MessageSquare,
  MonitorPlay,
  RadioTower,
  Mail,
  Router,
  ScrollText,
  ShieldCheck,
  Users,
  UserCog,
  X,
} from 'lucide-react';
import { useAuth } from '../contexts/use-auth';
import { ThemeToggle } from './ThemeToggle';
import { cn } from '../lib/utils';
import type { Permission } from '../lib/permissions';

type NavItem = {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
  requires?: Permission;
};
type NavSection = { label?: string; items: NavItem[] };

const sections: NavSection[] = [
  {
    items: [{ to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true }],
  },
  {
    label: 'X5 Setup',
    items: [
      { to: '/organizations', label: 'Organizations', icon: Building2, requires: 'x5.read' },
      { to: '/users', label: 'Users', icon: Users, requires: 'x5.read' },
      { to: '/applications', label: 'Applications', icon: Boxes, requires: 'x5.read' },
    ],
  },
  {
    label: 'Reporting',
    items: [
      { to: '/reporting', label: 'Reports', icon: FileBarChart, requires: 'reporting.read' },
      { to: '/reporting/status', label: 'Status', icon: Activity, requires: 'reports.read' },
    ],
  },
  {
    label: 'Datalake',
    items: [
      { to: '/datalake/status', label: 'Status', icon: Activity, requires: 'datalake.read' },
      { to: '/datalake/runs', label: 'Runs', icon: GitBranch, requires: 'datalake.read' },
      { to: '/datalake/customers', label: 'Customers', icon: Building2, requires: 'datalake.read' },
      { to: '/datalake/coverage', label: 'Coverage', icon: Layers, requires: 'datalake.read' },
    ],
  },
  {
    label: 'WorkSpaces Apps',
    items: [
      { to: '/workspaces/sessions', label: 'Sessions', icon: MonitorPlay, requires: 'appstream.read' },
      { to: '/workspaces/usage', label: 'Usage', icon: Activity, requires: 'appstream.read' },
    ],
  },
  {
    label: 'SMS Numbers',
    items: [
      { to: '/sms/numbers', label: 'Numbers', icon: MessageSquare, requires: 'sms.read' },
      { to: '/sms/report', label: 'Report', icon: FileBarChart, requires: 'sms.read' },
    ],
  },
  {
    label: 'Cellular',
    items: [
      { to: '/cellular', label: 'SIMs & Usage', icon: RadioTower, requires: 'cellular.read' },
      { to: '/cellular/notices', label: 'Notices', icon: Mail, requires: 'cellular.read' },
    ],
  },
  {
    label: 'Gateways',
    items: [
      { to: '/gateways', label: 'Teltonika Gateways', icon: Router, requires: 'gateway.read' },
      { to: '/gateways/history', label: 'Command History', icon: History, requires: 'gateway.read' },
    ],
  },
  {
    label: 'Exporter',
    items: [{ to: '/exporter', label: 'Data Exporter', icon: FileArchive, requires: 'exporter.read' }],
  },
  {
    label: 'Administration',
    items: [
      { to: '/admin/users', label: 'Admin Users', icon: UserCog, requires: 'admin.users.read' },
      { to: '/admin/roles', label: 'Roles', icon: ShieldCheck, requires: 'admin.roles.read' },
      { to: '/admin/audit', label: 'Audit Log', icon: ScrollText, requires: 'audit.read' },
    ],
  },
];

interface Crumb {
  label: string;
  to?: string;
  mono?: boolean;
}

// The top bar says where you are rather than repeating the page's own <h1>. Only three routes
// nest, so the trail is at most two deep; anything else falls through to the flat lookup.
function crumbsFor(pathname: string): Crumb[] {
  const org = /^\/organizations\/(.+)$/.exec(pathname)?.[1];
  if (org) return [{ label: 'Organizations', to: '/organizations' }, { label: decodeURIComponent(org), mono: true }];

  const report = /^\/reporting\/([^/]+)$/.exec(pathname)?.[1];
  if (report && report !== 'status') {
    return [{ label: 'Reports', to: '/reporting' }, { label: decodeURIComponent(report), mono: true }];
  }

  const item = sections.flatMap((s) => s.items).find((i) => i.to === pathname);
  const section = sections.find((s) => s.items.some((i) => i.to === pathname));
  // Datalake and Reporting both have a page called "Status"; the section name is what
  // disambiguates them.
  if (item && section?.label && item.label !== section.label) {
    return [{ label: section.label }, { label: item.label }];
  }
  return [{ label: item?.label ?? 'Dashboard' }];
}

export function Layout() {
  const { can } = useAuth();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const visibleSections = sections
    .map((s) => ({ ...s, items: s.items.filter((i) => !i.requires || can(i.requires)) }))
    .filter((s) => s.items.length > 0);
  const crumbs = crumbsFor(pathname);

  return (
    <div className="flex h-screen overflow-hidden bg-background text-[15px] text-foreground">
      <aside className="hidden w-53 shrink-0 flex-col border-r border-line-soft bg-chrome lg:flex">
        <Brand />
        <SidebarNav sections={visibleSections} />
        <SidebarFooter />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <div className="absolute inset-y-0 left-0 flex w-60 flex-col bg-chrome">
            <div className="flex items-center justify-between pr-2">
              <Brand />
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu"
                className="text-white/40 transition-colors hover:text-white/90"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <SidebarNav sections={visibleSections} onNavigate={() => setMobileOpen(false)} />
            <SidebarFooter />
          </div>
        </div>
      )}

      <main className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-12 shrink-0 items-center gap-3 border-b border-line bg-panel px-3.5">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            className="text-muted-foreground transition-colors hover:text-foreground lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-muted-foreground">
            {crumbs.map((crumb, i) => (
              <span key={`${crumb.label}-${i}`} className="flex items-center gap-1.5">
                {i > 0 ? <ChevronRight className="h-3 w-3" /> : null}
                {crumb.to ? (
                  <RouterLink to={crumb.to} className="transition-colors hover:text-foreground">
                    {crumb.label}
                  </RouterLink>
                ) : (
                  <span className={cn(crumb.mono ? 'font-mono text-foreground' : 'text-foreground')}>
                    {crumb.label}
                  </span>
                )}
              </span>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
          </div>
        </header>
        <div className="min-h-0 flex-1 overflow-auto p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2 px-3.5 pt-3.5 pb-3">
      <div className="flex h-6 w-6 items-center justify-center rounded-[5px] bg-accent text-[13px] font-bold tracking-tight text-white">
        X
      </div>
      <div className="leading-tight">
        <div className="text-sm font-semibold text-white">x5-admin</div>
        <div className="text-[12px] tracking-wider text-white/40 uppercase">Internal</div>
      </div>
    </div>
  );
}

function SidebarNav({ sections, onNavigate }: { sections: NavSection[]; onNavigate?: () => void }) {
  return (
    <nav className="min-h-0 flex-1 overflow-y-auto pb-2">
      {sections.map((section, i) => (
        <div key={section.label ?? i} className={cn('flex flex-col gap-px px-2', section.label && 'mt-4')}>
          {section.label ? (
            <div className="px-2.5 py-1.5 text-[12px] font-semibold tracking-[0.09em] text-white/30 uppercase">
              {section.label}
            </div>
          ) : null}
          {section.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm transition-colors',
                  isActive
                    ? 'bg-white/8 font-semibold text-white shadow-[inset_2px_0_0_var(--color-accent)]'
                    : 'text-white/55 hover:bg-white/5 hover:text-white/90',
                )
              }
            >
              <item.icon className="h-4 w-4 shrink-0" />
              {item.label}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
}

function SidebarFooter() {
  const { email, role, name, picture, signOut } = useAuth();
  return (
    <div className="mt-auto border-t border-white/7 p-2.5">
      <div className="flex items-center gap-2 px-1 py-1.5">
        <Avatar email={email} name={name} picture={picture} />
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate text-[13px] font-medium text-white/90">{name ?? email ?? '—'}</div>
          <div className="truncate text-[12px] text-white/40">{role ?? 'No role'}</div>
        </div>
        <button
          type="button"
          onClick={() => void signOut()}
          title="Sign out"
          aria-label="Sign out"
          className="shrink-0 text-white/35 transition-colors hover:text-white/80"
        >
          <LogOut className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function Avatar({
  email,
  name,
  picture,
}: {
  email: string | null;
  name: string | null;
  picture: string | null;
}) {
  const [failed, setFailed] = useState(false);
  const fallbackChar = (name ?? email ?? '?').charAt(0).toUpperCase();

  if (picture && !failed) {
    return (
      <img
        src={picture}
        alt={name ?? email ?? 'avatar'}
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
        className="h-7 w-7 shrink-0 rounded-full object-cover"
      />
    );
  }
  return (
    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/10 text-[12px] font-semibold text-white/75">
      {fallbackChar}
    </div>
  );
}
